import { useCallback, useEffect, useRef, useState } from "react";
import { useCart } from "../cart/CartContext";
import ProductGallery from "../components/ProductGallery";
import StoreHeader from "../components/StoreHeader";
import VariantPicker from "../components/VariantPicker";
import { useProductDetails } from "../hooks/useProductDetails";

const moneyFormatters = new Map();

function formatMoney(amountMinor, currency = "BRL") {
  const key = `${currency}:pt-BR`;
  if (!moneyFormatters.has(key)) {
    moneyFormatters.set(
      key,
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency,
      })
    );
  }
  return moneyFormatters.get(key).format(Number(amountMinor) / 100);
}

function stockDetails(variant) {
  if (!variant) return null;
  if (variant.stockStatus === "in_stock") {
    return {
      label: "Em estoque",
      detail:
        Number(variant.availableQuantity) > 0
          ? `${variant.availableQuantity} unidades disponíveis`
          : null,
      tone: "available",
    };
  }
  if (variant.stockStatus === "backorder") {
    return {
      label: "Disponível sob encomenda",
      detail: null,
      tone: "backorder",
    };
  }
  return { label: "Produto indisponível", detail: null, tone: "unavailable" };
}

function ProductDetailSkeleton() {
  return (
    <div className="product-detail product-detail--loading">
      <div className="skeleton product-detail-skeleton__media" />
      <div>
        <div className="skeleton product-detail-skeleton__eyebrow" />
        <div className="skeleton product-detail-skeleton__title" />
        <div className="skeleton product-detail-skeleton__text" />
        <div className="skeleton product-detail-skeleton__price" />
        <div className="skeleton product-detail-skeleton__options" />
      </div>
    </div>
  );
}

export default function ProductDetailPage({ storeSlug, productHandle }) {
  const { addItem } = useCart();
  const { store, product, loading, error, retry } = useProductDetails(
    storeSlug,
    productHandle
  );
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const confirmationTimerRef = useRef(null);
  const handleVariantChange = useCallback(
    (variant) => setSelectedVariant(variant),
    []
  );
  const availability = stockDetails(selectedVariant);
  const category = product?.categories?.[0];
  const storeUrl = `/commerce/${encodeURIComponent(storeSlug)}`;

  useEffect(
    () => () => {
      window.clearTimeout(confirmationTimerRef.current);
    },
    []
  );

  const addSelectedVariant = () => {
    if (!addItem(product, selectedVariant)) return;
    setAddedToCart(true);
    window.clearTimeout(confirmationTimerRef.current);
    confirmationTimerRef.current = window.setTimeout(
      () => setAddedToCart(false),
      1800
    );
  };

  return (
    <div className="storefront">
      <StoreHeader store={store} storeSlug={storeSlug} />

      <main className="product-page">
        <nav className="breadcrumb" aria-label="Navegação estrutural">
          <a href={storeUrl}>
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="m15 18-6-6 6-6" />
            </svg>
            Todos os produtos
          </a>
          {product && <span aria-hidden>/</span>}
          {product && <span>{product.title}</span>}
        </nav>

        {loading ? (
          <ProductDetailSkeleton />
        ) : error ? (
          <section className="state-card" role="alert">
            <h1>
              {error.status === 404
                ? "Produto não encontrado"
                : "Não foi possível carregar o produto"}
            </h1>
            <p>
              {error.status === 404
                ? "Este produto não está disponível ou o endereço está incorreto."
                : error.message}
            </p>
            <button type="button" onClick={retry}>
              Tentar novamente
            </button>
          </section>
        ) : (
          <div className="product-detail">
            <ProductGallery
              images={product.images}
              productTitle={product.title}
            />

            <section className="product-information">
              <div className="product-information__intro">
                <div className="product-information__meta">
                  {category && <span>{category.name}</span>}
                  {product.brand && <span>{product.brand}</span>}
                </div>
                <h1>{product.title}</h1>
                {product.description && (
                  <p className="product-information__description">
                    {product.description}
                  </p>
                )}
              </div>

              {product.variants.length > 0 && (
                <div className="product-purchase-details">
                  {selectedVariant && (
                    <div className="product-detail-price">
                      <p>
                        {formatMoney(
                          selectedVariant.amountMinor,
                          selectedVariant.currency
                        )}
                      </p>
                      {selectedVariant.compareAtAmountMinor != null &&
                        Number(selectedVariant.compareAtAmountMinor) >
                          Number(selectedVariant.amountMinor) && (
                          <span>
                            {formatMoney(
                              selectedVariant.compareAtAmountMinor,
                              selectedVariant.currency
                            )}
                          </span>
                        )}
                    </div>
                  )}

                  <VariantPicker
                    variants={product.variants}
                    onChange={handleVariantChange}
                  />

                  {selectedVariant && (
                    <>
                      <div className="selected-variant-summary">
                        <div>
                          <span
                            className={`stock-dot stock-dot--${availability?.tone}`}
                          />
                          <strong>{availability?.label}</strong>
                          {availability?.detail && (
                            <small>{availability.detail}</small>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        className={
                          addedToCart
                            ? "add-to-cart-button add-to-cart-button--confirmed"
                            : "add-to-cart-button"
                        }
                        disabled={
                          selectedVariant.stockStatus === "out_of_stock"
                        }
                        onClick={addSelectedVariant}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden>
                          {addedToCart ? (
                            <path d="m5 12 4 4L19 6" />
                          ) : (
                            <path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L20.2 8H6.1M12 8v5m-2.5-2.5h5" />
                          )}
                        </svg>
                        {selectedVariant.stockStatus === "out_of_stock"
                          ? "Produto indisponível"
                          : addedToCart
                            ? "Adicionado ao carrinho"
                            : "Adicionar ao carrinho"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      <footer className="store-footer">
        <span>Loja criada com</span>
        <strong>AtomicPay</strong>
      </footer>
    </div>
  );
}
